import {
  autoPlacement,
  autoUpdate,
  offset,
  useFloating,
} from '@floating-ui/react-dom';
import { Popover, PopoverButton } from '@headlessui/react';
import type { RowData } from '@tanstack/react-table';
import { cn } from 'cn';
import { useTranslation } from 'react-i18next';
import Button from '../../Button/Button';
import Icon from '../../Icon';
import type { ColumnFilterAndSortPopoverProps } from './columnHeaderControlsShared';
import { getColumnFilterState } from './columnHeaderControlsShared';
import { FilterPopover } from './FilterPopover';
import { getUniqueFilterOptions } from './filterOptions';
import { ImmediateCheckboxFilter } from './ImmediateCheckboxFilter';
import { SortButton } from './SortButton';

export function ColumnControls<TData extends RowData>(
  props: ColumnFilterAndSortPopoverProps<TData>,
) {
  const { t } = useTranslation('ui');
  const { refs, floatingStyles } = useFloating({
    middleware: [
      autoPlacement({
        alignment: props.alignment || 'start',
        allowedPlacements: [
          'bottom-end',
          'bottom-start',
          'top-end',
          'top-start',
        ],
      }),
      offset(() => 15),
    ],
    whileElementsMounted: autoUpdate,
  });

  const { selectedFilterCount, filterCountBadge } = getColumnFilterState(props);
  const optionCount = getUniqueFilterOptions(props.data, props.column).length;
  const isDisabled = optionCount === 0;

  return (
    <Popover>
      {({ open, close }) => {
        const refreshState = isDisabled
          ? 'disabled'
          : open
            ? 'open'
            : selectedFilterCount > 0
              ? 'selected'
              : 'default';

        return (
          <>
            <span className="flex items-center">
              {!props.isSortable ? (
                <span className={cn('truncate pr-1', 'kunde:pr-1.5')}>
                  {props.label}
                </span>
              ) : null}
              {props.column
                ? (() => {
                    const column = props.column;
                    return (
                      <SortButton
                        columnId={column.id}
                        isSortable={props.isSortable}
                        onClick={column.getToggleSortingHandler()}
                        getNextSortingOrder={() => column.getNextSortingOrder()}
                        sortDirection={props.sortDirection}
                        label={props.label}
                      />
                    );
                  })()
                : null}
              <span ref={refs.setReference}>
                <PopoverButton
                  as={Button}
                  theme="subtle"
                  compact
                  hasIcon
                  hasLabel={false}
                  disabled={isDisabled}
                  aria-label={t('filter')}
                  data-filter-state={refreshState}
                  data-filter-count={
                    selectedFilterCount > 0
                      ? selectedFilterCount.toString()
                      : undefined
                  }
                  data-testid={`filter-button-${props.column?.id}`}
                  className={cn(
                    'border p-1',
                    'self-center border-transparent bg-transparent text-neutral-100 outline-hidden',
                    'hover:text-blue-60 hover:bg-neutral-10',
                    'focus-visible:ring-blue-20 focus-visible:bg-transparent',
                    'disabled:text-neutral-40',
                    {
                      'bg-neutral-10 text-blue-100 kunde:text-blue-100 kunde:bg-blue-10':
                        refreshState === 'open',
                      'text-blue-100 kunde:text-blue-100 kunde:bg-inherit':
                        refreshState === 'selected',
                      'kunde:bg-inherit kunde:text-neutral-100':
                        refreshState === 'default',
                    },
                    'hover:kunde:text-blue-60 hover:kunde:bg-blue-10 active:kunde:bg-blue-10',
                    'disabled:kunde:bg-transparent disabled:kunde:text-neutral-60',
                  )}
                >
                  <span className="flex items-center">
                    <Icon
                      type="sort"
                      size="tiny"
                      solid={open || selectedFilterCount > 0}
                      className="shrink-0"
                    />
                    {selectedFilterCount > 0 ? (
                      <span
                        data-testid={`filter-badge-${props.column?.id}`}
                        className="typ-ui-sm text-left font-medium leading-none"
                      >
                        {filterCountBadge}
                      </span>
                    ) : null}
                  </span>
                </PopoverButton>
              </span>
            </span>

            <FilterPopover
              column={props.column}
              floatingStyles={floatingStyles}
              open={open}
              setFloating={refs.setFloating}
            >
              <ImmediateCheckboxFilter
                data={props.data}
                column={props.column}
                filterValue={props.filterValue}
                close={close}
                label={props.label}
              />
            </FilterPopover>
          </>
        );
      }}
    </Popover>
  );
}
